import useHousePrices from './useHousePrices';
import { useDerivedSales } from './useDerivedSales';
import { useQuery } from 'react-query';
import { calculateYearsSinceSale, getGrowthRate } from './helpers/priceEstimationHelpers';
import { usePreparePropertyChartData } from './usePreparePropertyChartData';
import { useGetChartData } from './useGetChartData';

export const useCalculateEstimatedCurrentPrice = ({ validatedAddress }) => {
  const postcode = validatedAddress?.postcode;
  const { data: housePricesData, isLoading: isLoadingHousePriceData } = useHousePrices(postcode);
  const { data: derivedData, isLoading: isLoadingDerviedSales } = useDerivedSales({
    validatedAddress,
  });

  const { data: propertyChartData, isLoading: isLoadingPropertyTrends } =
    usePreparePropertyChartData({
      validatedAddress,
      postcode,
    });

  const { data: chartData, isLoading: isLoadingChartData } = useGetChartData({
    validatedAddress,
    postcode,
  });

  const safePropertyChartData =
    isLoadingPropertyTrends || !propertyChartData
      ? { propertyTrends: null, areaSales: null }
      : propertyChartData;
  const { propertyTrends } = safePropertyChartData;

  const safeDerivedData =
    isLoadingDerviedSales || !derivedData
      ? { currentPropertySales: null, areaSales: null, propertyAddress: null }
      : derivedData;
  const { areaSales } = safeDerivedData;
  return useQuery(
    ['calculateEstimatedCurrentPrice', validatedAddress],
    () => {
      if (!propertyTrends) return 0;
      const { firstSale, lastSale, salesCount } = propertyTrends;
      if (salesCount > 0 && (lastSale || firstSale)) {
        const sale = lastSale || firstSale;
        if (propertyChartData) {
          const yearsSinceSale = calculateYearsSinceSale(sale.x);
          const annualGrowthRate = getGrowthRate({
            propertyChartData,
            chartData,
            housePriceData: housePricesData,
          });
          if (annualGrowthRate !== undefined && annualGrowthRate !== null)
            return Math.round(sale.y * Math.pow(1 + annualGrowthRate, yearsSinceSale));
        }
        return sale.y;
      }
      if (areaSales && areaSales.length > 0 && propertyChartData?.currentPropertyType) {
        const sameTypeAreaSales = areaSales.filter((sale) => {
          const salePropertyType = (sale.propertyType || '').toLowerCase();
          const currentType = propertyChartData.currentPropertyType.toLowerCase();
          return (
            salePropertyType === currentType ||
            salePropertyType.includes(currentType) ||
            currentType.includes(salePropertyType)
          );
        });
        if (sameTypeAreaSales.length > 0) {
          const mostRecentSale = sameTypeAreaSales.sort(
            (a, b) => new Date(b.date) - new Date(a.date),
          )[0];
          if (
            housePricesData &&
            housePricesData.regionPriceIndex &&
            housePricesData.regionPriceIndex.increases &&
            housePricesData.regionPriceIndex.increases.byType
          ) {
            const yearsSinceSale = calculateYearsSinceSale(new Date(mostRecentSale.date));
            const regionalAnnualGrowthRate = getGrowthRate({
              propertyChartData,
              chartData,
              housePriceData: housePricesData,
              regional: true,
            });
            if (regionalAnnualGrowthRate !== undefined && regionalAnnualGrowthRate !== null)
              return Math.round(
                mostRecentSale.price * Math.pow(1 + regionalAnnualGrowthRate, yearsSinceSale),
              );
          }
          return Math.round(mostRecentSale.price);
        }
      }
      if (
        propertyChartData?.currentPropertyType &&
        housePricesData &&
        housePricesData.regionPriceIndex
      ) {
        const propertyTypeMap = {
          detached: 'averagePriceDetached',
          'semi-detached': 'averagePriceSemiDetached',
          terraced: 'averagePriceTerraced',
          flat: 'averagePriceFlat',
          maisonette: 'averagePriceFlat',
        };
        const normalizedType = propertyChartData.currentPropertyType.toLowerCase();
        let priceField = null;
        for (const [typeKey, fieldName] of Object.entries(propertyTypeMap)) {
          if (normalizedType.includes(typeKey)) {
            priceField = fieldName;
            break;
          }
        }
        if (
          priceField &&
          housePricesData.regionPriceIndex.monthlyData &&
          housePricesData.regionPriceIndex.monthlyData.length > 0
        ) {
          const latestMonthData = [...housePricesData.regionPriceIndex.monthlyData].sort(
            (a, b) => new Date(b.date) - new Date(a.date),
          )[0];
          if (latestMonthData && latestMonthData[priceField]) {
            return Math.round(latestMonthData[priceField]);
          }
        }
        if (
          housePricesData.regionPriceIndex.monthlyData &&
          housePricesData.regionPriceIndex.monthlyData.length > 0
        ) {
          const latestMonthData = [...housePricesData.regionPriceIndex.monthlyData].sort(
            (a, b) => new Date(b.date) - new Date(a.date),
          )[0];
          if (latestMonthData && latestMonthData.averagePrice) {
            return Math.round(latestMonthData.averagePrice);
          }
        }
      }
      return 0;
    },
    {
      enabled:
        !!propertyTrends &&
        !isLoadingHousePriceData &&
        !isLoadingPropertyTrends &&
        !isLoadingDerviedSales &&
        !isLoadingChartData,
      staleTime: Infinity,
    },
  );
};
